/* eslint-disable react/display-name */
import { Button, DatePicker, Form, InputNumber, Popconfirm, Space, Table, TableProps, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'src/index';
import { useCompanyParametersCheckins } from 'src/features/companyParameters/companyParameters.hooks';
import { CompanyParametersRecord } from 'src/features/companyParameters/companyParameters.types';
import { useIntl } from 'react-intl';
import {
  createCompanyCheckinRecord,
  deleteCompanyCheckinRecord,
} from 'src/features/companyParameters/companyParameters.state';
import { CheckOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useCompanyById } from 'src/features/companies/companies.hooks';
import moment from 'moment';
import { Company } from 'src/features/companies/companies.types';

interface CreateCheckinFormProps {
  onCreate: (values: { date: string; value: number }) => void;
  onCancel: () => void;
  loading?: boolean;
  disabledDate?: string;
}

const CreateCheckinForm: React.FC<CreateCheckinFormProps> = props => {
  const { loading } = props;
  const intl = useIntl();
  const [form] = Form.useForm();
  const [, forceUpdate] = useState({});
  useEffect(() => {
    forceUpdate({});
  }, []);
  const onFinish = (values: Record<string, any>) =>
    props.onCreate({
      value: values.value as number,
      date: values['date'].format('YYYY-MM-DD') as string,
    });

  function disabledDate(current: moment.Moment) {
    return current < moment(props.disabledDate).startOf('day');
  }

  return (
    <Form name={'createFeed'} layout='inline' form={form} onFinish={onFinish} style={{ width: '100%' }}>
      <Space direction={'horizontal'} style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space size={0}>
          <Form.Item
            name={'date'}
            required
            rules={[
              {
                required: true,
                message: undefined,
              },
            ]}
          >
            <DatePicker
              autoFocus
              disabled={loading}
              style={{ width: '200' }}
              placeholder='Дата'
              defaultOpen
              disabledDate={current => current < moment(props.disabledDate).startOf('day')}
            />
          </Form.Item>
          <Form.Item
            name={'value'}
            required
            rules={[
              {
                required: true,
                message: undefined,
              },
            ]}
          >
            <InputNumber
              disabled={loading}
              style={{ width: '60', textAlign: 'right' }}
              max={100}
              min={0}
              placeholder='Процент'
            />
          </Form.Item>
        </Space>
        <Space size={0}>
          <Form.Item shouldUpdate style={{ margin: 2 }}>
            {() => (
              <Button
                type='primary'
                htmlType='submit'
                disabled={
                  !form.isFieldsTouched(true) || !!form.getFieldsError().filter(({ errors }) => errors.length).length
                }
                loading={loading}
                icon={<CheckOutlined />}
              />
            )}
          </Form.Item>
          <Form.Item style={{ margin: 0 }}>
            <Button onClick={props.onCancel} disabled={loading} icon={<CloseOutlined />} />
          </Form.Item>
        </Space>
      </Space>
    </Form>
  );
};

const CompanyCheckinPercentage: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const company = useCompanyById(companyId) as Company;
  const dispatch: AppDispatch = useDispatch();
  const items = useCompanyParametersCheckins(companyId);
  const [isCreateCreateMode, setCreateMode] = useState(false);

  const handleAddCheckin = useCallback(
    async (data: { date: string; value: number }) => {
      await dispatch(createCompanyCheckinRecord({ companyId, data }));
      setCreateMode(false);
    },
    [dispatch, companyId]
  );

  const handleRemoveCheckin = useCallback(
    (id: string) => {
      dispatch(deleteCompanyCheckinRecord({ companyId, recordId: id }));
    },
    [dispatch, companyId]
  );

  const tableConfig = useMemo<TableProps<CompanyParametersRecord>>(
    () => ({
      dataSource: items || [],
      rowKey: values => values?.id,
      columns: [
        {
          title: 'Дата',
          dataIndex: 'date',
          key: 'date',
          type: 'date',
        },
        {
          title: 'Процент',
          dataIndex: 'value',
          key: 'value',
          align: 'right',
          type: 'number',
        },
        {
          title: 'Действия',
          dataIndex: 'action',
          align: 'center',
          width: 100,
          render: (_, record) => (
            <Popconfirm title='Уверенны, что хотите удалить?' onConfirm={() => handleRemoveCheckin(record.id)}>
              <Button danger icon={<CloseOutlined />} />
            </Popconfirm>
          ),
        },
      ],
    }),
    [items, handleRemoveCheckin]
  );

  return (
    <Space style={{ width: '100%' }} direction={'vertical'}>
      <Space direction={'horizontal'} style={{ width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Заселяемость
        </Typography.Title>
        {isCreateCreateMode ? (
          <CreateCheckinForm
            onCancel={() => setCreateMode(false)}
            onCreate={handleAddCheckin}
            disabledDate={company.activatedAt as string}
          />
        ) : (
          <Space direction={'horizontal'} style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button icon={<PlusOutlined />} onClick={() => setCreateMode(true)} />
          </Space>
        )}
      </Space>
      <Table {...tableConfig} sticky showSorterTooltip={false} />
    </Space>
  );
};

export default CompanyCheckinPercentage;
